import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ChevronDown, ChevronRight, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'

import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import Tooltip from 'components/reusable/Tooltip'
import { adminLedgerAccountsV2 } from 'rails_routes'

import LedgerPathDisplay from './LedgerPathDisplay'
import { type LedgerAccount } from './types'
import { useGetLedgerAccounts } from '../../hooks/useGetLedgerAccounts'
import { formatMoney } from '../../utils'
import { filterAccountsByBalance } from '../../utils/ledgerFilters'
import { buildTreeData, type DisplayAccount } from '../../utils/ledgerTree'
import TanstackTable from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<LedgerAccount | DisplayAccount>()

const DEFAULT_PAGE_SIZE = 50

interface LedgerAccountListProps {
  ledgerAccountId?: string
  query?: string
  cutoff?: string
  minBalance?: string
  maxBalance?: string
  hideZero?: boolean
}

function LedgerAccountListImpl ({ ledgerAccountId, query, cutoff, minBalance, maxBalance, hideZero }: LedgerAccountListProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [treeViewEnabled, setTreeViewEnabled] = useState(false)
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set())

  const toggleExpanded = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  const columns = [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: (info) => (
        <div className='flex items-center justify-between'>
          <Tooltip triggerChildren={
            <a href={adminLedgerAccountsV2.index({ query: { id: info.getValue() } })}>
              {info.getValue().substring(info.getValue().length - 5)}
            </a>
          }
          >
            <div className='text-xs p-2 rounded-md text-left'>
              <div><strong>Account Type:</strong> {info.row.original.account_type}</div>
              <div><strong>Owner ID:</strong> {info.row.original.owner_id || 'None'}</div>
              <div><strong>Owner Type:</strong> {info.row.original.owner_type || 'None'}</div>
            </div>
          </Tooltip>
          <CopyButtonWithAnimation content={info.getValue()}>
            <Copy size={16} strokeWidth={1} />
          </CopyButtonWithAnimation>
        </div>
      )
    }),
    columnHelper.accessor('entry_sum', {
      header: 'Balance',
      cell: (info) => formatMoney(info.getValue()),
    }),
    columnHelper.accessor('path', {
      header: 'Path',
      cell: (info) => {
        const account = info.row.original as DisplayAccount
        const isExpanded = expandedPaths.has(info.getValue())

        return (
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-1'>
              {treeViewEnabled && (
                <>
                  <div style={{ width: `${account.depth * 20}px` }} />
                  {account.hasChildren
                    ? (
                      <button
                        onClick={() => toggleExpanded(info.getValue())}
                        className='p-1 hover:bg-gray-100 rounded'
                      >
                        {isExpanded
                          ? (
                            <ChevronDown size={16} className='text-gray-600' />
                            )
                          : (
                            <ChevronRight size={16} className='text-gray-600' />
                            )}
                      </button>
                      )
                    : (
                      <div style={{ width: '24px' }} />
                      )}
                </>
              )}
              <LedgerPathDisplay path={info.getValue()} showOnlyLastSegment={treeViewEnabled} />
            </div>
            <CopyButtonWithAnimation content={info.getValue()}>
              <Copy size={16} strokeWidth={1} />
            </CopyButtonWithAnimation>
          </div>
        )
      },
    }),
    columnHelper.accessor('updated_at', {
      header: 'Updated Date',
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
  ]

  // Set all filters from the URL on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('page')
    const pageSizeParam = params.get('page_size')
    const sortParam = params.get('sort')

    if (pageParam) {
      setPagination(prev => ({
        ...prev,
        pageIndex: parseInt(pageParam, 10)
      }))
    }

    if (pageSizeParam) {
      setPagination(prev => ({
        ...prev,
        pageSize: parseInt(pageSizeParam, 10)
      }))
    }

    // Parse sort parameter (e.g., "path" or "-path")
    if (sortParam) {
      const isDescending = sortParam.startsWith('-')
      const column = isDescending ? sortParam.substring(1) : sortParam
      setSorting([{ id: column, desc: isDescending }])
    }
  }, [])

  // Update the URL on pagination and sorting change
  useEffect(() => {
    const pageURL = new URL(window.location.href)
    if (pagination.pageIndex) { pageURL.searchParams.set('page', pagination.pageIndex.toString()) }

    // Update sort parameter
    if (sorting.length > 0) {
      const sortColumn = sorting[0].id
      const sortValue = sorting[0].desc ? `-${sortColumn}` : sortColumn
      pageURL.searchParams.set('sort', sortValue)
    } else {
      pageURL.searchParams.delete('sort')
    }

    // Use replaceState instead of pushState to avoid polluting browser history
    window.history.replaceState({}, '', pageURL)
  }, [pagination, sorting])

  const { data: ledgerAccountsData, isLoading, isError } = useGetLedgerAccounts({
    id: ledgerAccountId,
    query,
    cutoff,
    pagination,
    sorting,
  })

  // Client-side filtering for balance ranges and zero balances
  const filteredData = filterAccountsByBalance(ledgerAccountsData?.data || [], {
    minBalance,
    maxBalance,
    hideZero,
  })

  // Build tree data for display
  const displayData = buildTreeData(filteredData, treeViewEnabled, expandedPaths)

  const table = useReactTable({
    data: displayData,
    columns,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      sorting,
      pagination,
    },
  })

  return (
    <div>
      <div className='mb-4'>
        <Tooltip triggerChildren={
          <label className='flex items-center gap-x-2 cursor-pointer w-fit'>
            <input
              type='checkbox'
              checked={treeViewEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTreeViewEnabled(e.target.checked)
                setExpandedPaths(new Set()) // Clear expansion state when toggling
              }}
              className='rounded border-gray-300'
            />
            <span className='text-sm text-gray-700'>Tree View</span>
          </label>
        }
        >
          <div className='text-xs p-2'>
            Display accounts in a hierarchical tree structure. Click arrows to expand/collapse child accounts.
          </div>
        </Tooltip>
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='No ledger accounts found.'
        setSorting={setSorting}
        defaultSortingState={[]}
        paginationLabelSingular='account'
        paginationLabelPlural='accounts'
        paginationMetadata={ledgerAccountsData?.pagination}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: ledgerAccountsData?.pagination.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: ledgerAccountsData?.pagination.prev_page || 1,
          })}
      />
    </div>
  )
}

const LedgerAccountList = ({ ledgerAccountId, query, cutoff, minBalance, maxBalance, hideZero }: LedgerAccountListProps) => {
  return (
    <LedgerAccountListImpl
      ledgerAccountId={ledgerAccountId}
      query={query}
      cutoff={cutoff}
      minBalance={minBalance}
      maxBalance={maxBalance}
      hideZero={hideZero}
    />
  )
}

export default LedgerAccountList
